import React, { type ReactNode } from 'react';

import ModalCore, { type ModalDialogProps } from '@atlaskit/modal-dialog';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

import { ModalBody } from './ModalBody';
import { ModalFooter } from './ModalFooter';
import { ModalTitle } from './ModalTitle';

export interface ModalProps extends ModalDialogProps {
	title: string;
	logo?: ReactNode;
	subtitle?: string;
	children: ReactNode;
	/**
	 * Wrap the actions in a Fragment so that spacing, wrapping, etc are handled by the modal.
	 */
	actions?: ReactNode;
	right?: ReactNode;
}

const container = xcss({
	overflowY: 'auto',
	height: '100%',
	[media.above.sm]: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
	},
});

const stack = xcss({
	height: '100%',
	padding: 'space.600',
	overflowY: 'auto',
});

const rightContainer = xcss({
	display: 'none',
	[media.above.sm]: {
		backgroundColor: 'color.background.accent.gray.subtlest',
		display: 'flex',
		width: '100%',
		overflowY: 'auto',
	},
});

export const Modal = ({
	logo,
	title,
	subtitle,
	children,
	actions,
	right,
	...modalProps
}: ModalProps) => {
	const includesRight = right !== undefined;

	return (
		<ModalCore width={includesRight ? 'x-large' : undefined} autoFocus={false} {...modalProps}>
			<Box xcss={includesRight && container}>
				<Stack space="space.400" xcss={stack}>
					{logo}
					<Stack space="space.200">
						<ModalTitle>{title}</ModalTitle>
						{subtitle ? (
							<Text color="color.text.subtle" size="medium">
								{subtitle}
							</Text>
						) : null}
					</Stack>
					<ModalBody>{children}</ModalBody>
					<ModalFooter>{actions}</ModalFooter>
				</Stack>
				{includesRight && <Box xcss={rightContainer}>{right}</Box>}
			</Box>
		</ModalCore>
	);
};
